<script setup>
import Icon from './ui/Icon.vue'
import PlanRow from './PlanRow.vue'
import {
  fmtGlobal,
  income,
  plannedExpenses,
  plannedIncome,
  planHealth,
  viewedMonth,
} from '../stores'
import { openSheet } from '../stores/state'
import { monthLabel } from '../lib/dates'
import { t } from '../stores/i18n'
</script>

<template>
  <section class="card relative overflow-hidden">
    <header class="flex items-center gap-3 border-b border-line-soft px-4 py-3.5">
      <div class="min-w-0 flex-1">
        <!-- Named, because nothing above the fold says which month this is. -->
        <h2 class="text-[15px] font-semibold">
          {{ t('plan.title') }} · {{ monthLabel(viewedMonth, { short: true }) }}
        </h2>
        <p class="tnum mt-0.5 text-[12px] text-faint">
          <template v-if="planHealth.total">
            {{ t('plan.seen', { matched: planHealth.matched, total: planHealth.total }) }}
            <template v-if="planHealth.missing">
              · {{ t('plan.missing', { count: planHealth.missing }) }}
            </template>
            <template v-if="planHealth.off">
              · {{ t('plan.offAmount', { count: planHealth.off }) }}
            </template>
          </template>
          <template v-else>{{ t('plan.nothing') }}</template>
        </p>
      </div>
      <!-- "New plan" rather than the bare noun: the tab strip already has a
           button reading "Plan", and the browser suite clicks buttons by text. -->
      <button class="btn-soft !px-2.5 !py-2 sm:!px-3.5" :aria-label="t('plan.addLabel')" @click="openSheet('plan')">
        <Icon name="plus" :size="16" /><span class="sr-only sm:not-sr-only">{{ t('plan.add') }}</span>
      </button>
    </header>

    <div v-if="plannedIncome.length" class="border-b border-line-soft">
      <div class="flex items-center justify-between px-4 pt-3 pb-1">
        <h3 class="text-[11.5px] font-semibold tracking-wide text-faint uppercase">
          {{ t('plan.income') }}
        </h3>
        <p class="tnum text-[12px]" :class="income.variance < -0.5 ? 'text-danger' : 'text-muted'">
          {{ t('common.xOfY', { x: fmtGlobal(income.actual, { round: true }), y: fmtGlobal(income.planned, { round: true }) }) }}
        </p>
      </div>
      <PlanRow v-for="row in plannedIncome" :key="row.id" :row="row" />
    </div>

    <div v-if="plannedExpenses.length">
      <h3 class="px-4 pt-3 pb-1 text-[11.5px] font-semibold tracking-wide text-faint uppercase">
        {{ t('plan.expenses') }}
      </h3>
      <PlanRow v-for="row in plannedExpenses" :key="row.id" :row="row" />
    </div>

    <div v-if="!plannedIncome.length && !plannedExpenses.length" class="px-6 py-10 text-center">
      <span class="mx-auto grid size-12 place-items-center rounded-2xl bg-raised text-faint">
        <Icon name="repeat" :size="22" />
      </span>
      <p class="mt-3 text-[14px] font-medium">{{ t('plan.empty') }}</p>
      <p class="mt-1 text-[13px] text-muted">
        {{ t('plan.emptyHint') }}
      </p>
      <div class="mt-4 flex justify-center gap-2">
        <button class="btn-primary" @click="openSheet('plan', { type: 'income' })">
          {{ t('plan.income') }}
        </button>
        <button class="btn-soft" @click="openSheet('plan', { type: 'expense' })">
          {{ t('kind.expense') }}
        </button>
      </div>
    </div>
  </section>
</template>
